import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Ticket, DollarSign, CheckCircle2 } from 'lucide-react';

const CURRENCY_SYMBOLS = {
  'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
  'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
};

// Calculate total price including platform fee (5.5%) when passed to buyers
// Service fee (13.5%) is ALWAYS deducted from organizer's payout, never passed to buyers
const calculateTotalWithFees = (basePrice, quantity, serviceFeeEnabled = false, absorbFees = true) => {
  const subtotal = basePrice * quantity;
  if (absorbFees) return subtotal;
  const platformFee = 0.055;
  return Math.ceil(subtotal * (1 + platformFee));
};

export function TicketCard({
  event,
  selectedTicket,
  setSelectedTicket,
  onGetTickets,
  isRecurringEvent,
  isFreeEvent,
  isReservationEvent,
  hasReservationLimit,
  totalAvailable,
  lowestPrice,
  hasFreeTIckets,
  getPriceDisplay,
  getButtonText
}) {
  const cs = CURRENCY_SYMBOLS[event?.currency] || '₦';
  return (
    <div className="lg:col-span-1">
      <Card className="sticky top-4 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-primary" />
            {isRecurringEvent ? 'Visit Info' : isReservationEvent ? 'Reservation' : 'Tickets'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Recurring Venue Info */}
          {isRecurringEvent ? (
            <div className="space-y-4">
              {event.price_range && (event.price_range.min_price > 0 || event.price_range.max_price > 0) ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Price Range</p>
                    <p className="font-semibold">
                      {cs}{event.price_range.min_price?.toLocaleString()} - {cs}{event.price_range.max_price?.toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Contact venue for pricing details</p>
                </div>
              )}
              
              {event.requires_rsvp && (
                <div className="flex items-center gap-2 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-400">RSVP Required</p>
                </div>
              )}
            </div>
          ) : (
            /* Regular Event Tickets */
            <div className="space-y-3">
              {event.ticket_types?.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedTicket?.ticket_id === ticket.ticket_id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  } ${ticket.available !== null && ticket.available !== undefined && ticket.available === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => (ticket.available === null || ticket.available === undefined || ticket.available > 0) && setSelectedTicket(ticket)}
                  data-testid={`ticket-option-${ticket.ticket_id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{ticket.name}</p>
                      <p className="text-sm text-muted-foreground">{ticket.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold font-mono text-lg">
                        {isFreeEvent || ticket.is_free ? (
                          <span className="text-secondary">Free</span>
                        ) : (
                          `${cs}${ticket.price.toLocaleString()}`
                        )}
                      </p>
                      {ticket.available !== null && ticket.available !== undefined && ticket.available === 0 ? (
                        <p className="text-xs text-destructive">Sold out</p>
                      ) : ticket.available === null || ticket.available === undefined ? (
                        <p className="text-xs text-green-500">Available</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reservation Info */}
          {isReservationEvent && hasReservationLimit && (
            <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Limited to {event.max_reservations} spots
              </p>
              <p className="text-xs text-blue-500 mt-1">
                Reserve your spot now
              </p>
            </div>
          )}

          {/* Free Ticket Limit Info - hidden for free events */}

          <Separator />
          
          {/* Price Summary */}
          {!isRecurringEvent && !isReservationEvent && (
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">
                {hasFreeTIckets ? 'Starting from' : 'From'}
              </span>
              <span className="text-2xl font-bold font-mono">
                {getPriceDisplay(selectedTicket)}
              </span>
            </div>
          )}

          {/* Fee Info */}
          {!isRecurringEvent && !isFreeEvent && !event.absorb_fees && selectedTicket && !selectedTicket.is_free && (
            <div className="text-xs text-muted-foreground text-center">
              <p>Price includes platform fee (5.5%)</p>
            </div>
          )}

          {/* CTA Button */}
          <Button 
            className="w-full h-12 text-lg gap-2"
            size="lg"
            disabled={totalAvailable === 0 && !isRecurringEvent}
            onClick={onGetTickets}
            data-testid="get-tickets-btn"
          >
            <Ticket className="w-5 h-5" />
            {getButtonText()}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
